import { ChakraBox, EquipUIFireflyV2Theme } from '@equip.health/ui';
import { DateTime } from 'luxon';

import { WEEK_VIEW_ITEM_HEIGHT } from '~/lib/constants/calendar.constants';
import { WeekViewLayoutUnit } from '~/lib/util/schedule/weekViewLayout.util';

import ScheduleWeekCancelledGroupItem from './ScheduleWeekCancelledGroupItem';
import ScheduleWeekEventItem from './ScheduleWeekEventItem';
import { getDisplayTitle } from '~/lib/util/schedule/schedule.util';

const TOP_LEVEL_ID = 'week-body-item';
const MINUTES_PER_SLOT = 30;
const {
  neutral: {
    line: neutralLine,
    background: { secondary: neutralBgSecondary },
  },
} = EquipUIFireflyV2Theme.colors;

const bodyBoxProps: Record<string, string> = {
  borderBottom: `1px solid ${neutralBgSecondary}`,
  borderRight: `1px solid ${neutralLine}`,
  height: `${WEEK_VIEW_ITEM_HEIGHT}px`,
  position: 'relative',
};

interface ScheduleWeekBodyItemProps {
  dateTime: string;
  isProvider?: boolean;
  /** Cards starting within this hour, already positioned by the day level layout */
  layoutUnits: WeekViewLayoutUnit[];
  onCancelledGroupClicked?: (startDateTime: string) => void;
  onScheduleItemClicked: (externalId: string) => void;
}

const ScheduleWeekBodyItem = ({
  dateTime,
  layoutUnits,
  onCancelledGroupClicked,
  onScheduleItemClicked,
  isProvider,
}: ScheduleWeekBodyItemProps) => {
  const renderHalfHourSlot = (isFirstHalfHour: boolean) =>
    layoutUnits
      .filter(({ startDateTime }) => {
        const { minute } = DateTime.fromISO(startDateTime);
        return isFirstHalfHour
          ? minute < MINUTES_PER_SLOT
          : minute >= MINUTES_PER_SLOT;
      })
      .map(({ cancelledItems, leftPercent, scheduledItem, width }) =>
        cancelledItems ? (
          <ScheduleWeekCancelledGroupItem
            cancelledItems={cancelledItems}
            key={`${TOP_LEVEL_ID}__cancelled-group--${cancelledItems[0].externalId}`}
            leftPercent={leftPercent}
            onCancelledGroupClicked={onCancelledGroupClicked}
            width={width}
          />
        ) : (
          <ScheduleWeekEventItem
            key={scheduledItem.externalId}
            leftPercent={leftPercent}
            onScheduleItemClicked={onScheduleItemClicked}
            scheduledItem={{
              ...scheduledItem,
              title: getDisplayTitle(scheduledItem, isProvider),
              width,
            }}
          />
        ),
      );

  return (
    <ChakraBox id={`${TOP_LEVEL_ID}__${dateTime}`}>
      <ChakraBox {...bodyBoxProps}>{renderHalfHourSlot(true)}</ChakraBox>
      <ChakraBox {...bodyBoxProps} borderBottom={`1px solid ${neutralLine}`}>
        {renderHalfHourSlot(false)}
      </ChakraBox>
    </ChakraBox>
  );
};

export default ScheduleWeekBodyItem;
